import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, CheckSquare, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;
const hdr = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' });

const MustReadPopup = () => {
  const [messages, setMessages] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    const check = async () => {
      try {
        const res = await fetch(`${API}/api/my/must-read-messages`, { headers: hdr() });
        if (res.ok) {
          const data = await res.json();
          if (data.length > 0) setMessages(data);
        }
      } catch (e) { console.error(e); }
    };
    check();
    const interval = setInterval(check, 30000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const acknowledge = async () => {
    const msg = messages[currentIdx];
    try {
      await fetch(`${API}/api/my/acknowledge-message/${msg.id}`, { method: 'POST', headers: hdr() });
      if (currentIdx < messages.length - 1) {
        setCurrentIdx(prev => prev + 1);
        setConfirmed(false);
      } else {
        setMessages([]);
        setCurrentIdx(0);
        setConfirmed(false);
      }
    } catch (e) { console.error(e); }
  };

  if (messages.length === 0) return null;
  const msg = messages[currentIdx];

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
        style={{ backgroundColor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }} data-testid="must-read-overlay">
        <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} className="w-full max-w-md bg-white rounded-2xl overflow-hidden shadow-2xl">
          {/* Orange top bar */}
          <div className="h-1.5 bg-orange-500" />
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm font-bold text-gray-900">Important Message from {msg.sender_name}</p>
                <p className="text-[10px] text-gray-400 uppercase font-medium">{msg.priority === 'urgent' ? 'Urgent' : 'Required Reading'}</p>
              </div>
            </div>

            <div className="p-4 rounded-xl bg-gray-50 border border-gray-100">
              <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{msg.content}</p>
            </div>

            {messages.length > 1 && (
              <p className="text-[10px] text-gray-400 text-center">Message {currentIdx + 1} of {messages.length}</p>
            )}

            <button onClick={() => setConfirmed(!confirmed)} className="flex items-center gap-3 w-full p-3 rounded-xl hover:bg-gray-50 transition-all text-left" data-testid="must-read-checkbox">
              {confirmed ? <CheckSquare className="w-5 h-5 text-orange-500 flex-shrink-0" /> : <Square className="w-5 h-5 text-gray-300 flex-shrink-0" />}
              <span className="text-sm text-gray-700 font-medium">I have read and understand this message</span>
            </button>

            <Button onClick={acknowledge} disabled={!confirmed}
              className="w-full py-5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="must-read-confirm-btn">
              Confirm & Continue
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default MustReadPopup;
